import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  HardHat,
  Home,
  ClipboardList,
  Users,
  Clock,
  StickyNote,
  Save,
  Briefcase,
  CalendarDays,
  ClipboardCheck,
  Download,
  Printer,
  TrendingUp,
  Settings,
  Send,
  Rocket,
  BookOpen,
  Zap,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

// Iter 61/69 — Tutorial guidato al primo accesso (completo o sintetico),
// bilingue, rivedibile dal Profilo, con "non mostrarlo più".
export const tourDoneKey = (email) => `il_tour_done_v1_${email || "anon"}`;

const FULL_BASE_ICONS = [Home, ClipboardList, Users, Clock, StickyNote, Save, Briefcase, CalendarDays];
const FULL_ADMIN_ICONS = [ClipboardCheck, Download, Printer, TrendingUp, Settings];
const SHORT_BASE_ICONS = [ClipboardList, CalendarDays, Briefcase];
const SHORT_ADMIN_ICONS = [ClipboardCheck, Printer];
const COLORS = ["#0044FF", "#7C3AED", "#0891B2", "#D97706", "#059669", "#DC2626", "#0044FF", "#7C3AED"];

const buildSteps = (t, mode, isAdmin) => {
  const base = t(mode === "full" ? "tour.fullBase" : "tour.shortBase", { returnObjects: true }) || [];
  const extraKey = isAdmin
    ? mode === "full" ? "tour.fullAdmin" : "tour.shortAdmin"
    : mode === "full" ? "tour.fullUser" : "tour.shortUser";
  const extra = t(extraKey, { returnObjects: true }) || [];
  const baseIcons = mode === "full" ? FULL_BASE_ICONS : SHORT_BASE_ICONS;
  const extraIcons = isAdmin
    ? mode === "full" ? FULL_ADMIN_ICONS : SHORT_ADMIN_ICONS
    : [Send];
  const end = t("tour.end", { returnObjects: true });
  const steps = [
    ...base.map((s, i) => ({ ...s, icon: baseIcons[i] || ClipboardList, color: COLORS[i % COLORS.length] })),
    ...extra.map((s, i) => ({ ...s, icon: extraIcons[i] || ClipboardCheck, color: COLORS[(i + 3) % COLORS.length] })),
    { ...end, icon: Rocket, color: "#0044FF" },
  ];
  return steps;
};

export default function OnboardingTour({ open, onClose }) {
  const { user } = useAuth();
  const { t } = useTranslation("app");
  const [mode, setMode] = useState(null); // null = schermata di scelta
  const [idx, setIdx] = useState(0);
  const [dontShow, setDontShow] = useState(true);

  useEffect(() => {
    if (open) {
      setMode(null);
      setIdx(0);
      setDontShow(true);
    }
  }, [open]);

  const markDone = () => {
    try {
      localStorage.setItem(tourDoneKey(user?.email), "1");
    } catch (_) { /* noop */ }
    window.dispatchEvent(new Event("il-tour-done"));
  };

  const close = (done) => {
    if (done) markDone();
    onClose?.();
  };

  const isAdmin = user?.role === "admin";
  const steps = mode ? buildSteps(t, mode, isAdmin) : [];
  const step = steps[idx];
  const last = mode && idx === steps.length - 1;
  const firstName = user?.name ? ", " + user.name.split(" ")[0] : "";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(dontShow); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="onboarding-tour-dialog"
      >
        {!mode ? (
          <div className="p-7 pb-5 space-y-4">
            <div className="text-center space-y-3">
              <div className="mx-auto h-16 w-16 rounded-2xl grid place-items-center bg-[#0044FF]/10">
                <HardHat className="h-8 w-8 text-[#0044FF]" strokeWidth={2.2} />
              </div>
              <h2
                className="font-heading font-black text-xl tracking-tight"
                data-testid="onboarding-tour-title"
              >
                {t("tour.welcomeTitle", { name: firstName })}
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("tour.welcomeSub")}
              </p>
            </div>
            <div className="space-y-2.5">
              <button
                type="button"
                onClick={() => { setMode("full"); setIdx(0); }}
                data-testid="tour-mode-full"
                className="w-full text-left rounded-xl border-2 border-[#0044FF] bg-blue-50/60 hover:bg-blue-50 p-4 flex items-start gap-3 transition-colors"
              >
                <BookOpen className="h-6 w-6 text-[#0044FF] shrink-0 mt-0.5" />
                <span>
                  <span className="block font-heading font-bold text-slate-900">{t("tour.fullBtn")}</span>
                  <span className="block text-xs text-slate-600 mt-0.5">{t("tour.fullBtnSub")}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => { setMode("short"); setIdx(0); }}
                data-testid="tour-mode-short"
                className="w-full text-left rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 p-4 flex items-start gap-3 transition-colors"
              >
                <Zap className="h-6 w-6 text-amber-500 shrink-0 mt-0.5" />
                <span>
                  <span className="block font-heading font-bold text-slate-900">{t("tour.shortBtn")}</span>
                  <span className="block text-xs text-slate-600 mt-0.5">{t("tour.shortBtnSub")}</span>
                </span>
              </button>
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={dontShow}
                  onChange={(e) => setDontShow(e.target.checked)}
                  data-testid="tour-dont-show-checkbox"
                  className="h-4 w-4"
                />
                {t("tour.dontShow")}
              </label>
              <button
                type="button"
                onClick={() => close(dontShow)}
                data-testid="onboarding-tour-skip"
                className="text-sm text-slate-500 underline hover:text-slate-700 shrink-0"
              >
                {t("tour.skip")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-8 pb-4 text-center space-y-4">
              <div
                className="mx-auto h-16 w-16 rounded-2xl grid place-items-center"
                style={{ backgroundColor: `${step.color}15` }}
              >
                <step.icon className="h-8 w-8" style={{ color: step.color }} strokeWidth={2.2} />
              </div>
              <h2
                className="font-heading font-black text-xl tracking-tight"
                data-testid="onboarding-tour-title"
              >
                {step.t}
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">{step.x}</p>
            </div>
            <div className="flex items-center justify-center gap-1.5 pb-4 flex-wrap px-4">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Step ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    i === idx ? "w-6 bg-[#0044FF]" : "w-2 bg-slate-300 hover:bg-slate-400"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 p-4">
              <Button
                variant="outline"
                onClick={() => (idx === 0 ? setMode(null) : setIdx(idx - 1))}
                data-testid="onboarding-tour-back"
                className="h-11"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> {t("tour.back")}
              </Button>
              <Button
                onClick={() => (last ? close(true) : setIdx(idx + 1))}
                data-testid="onboarding-tour-next"
                className="h-11 flex-1 max-w-[200px] bg-[#0044FF] hover:bg-[#0033CC] font-bold"
              >
                {last ? t("tour.start") : t("tour.next")}
                {!last && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
